import {useQuery} from '@github-ui/react-query'
import {IssueTimelineItemCounts} from '@github-ui/server-persisted-queries'
import type {IssueTimelineItemCountsQuery} from '@github-ui/server-persisted-queries/types/IssueTimelineItemCounts'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {ACTIVITY_FILTER_CATEGORIES, type ActivityFilterCategory} from './activity-filters'

export type ActivityFilterCounts = Record<ActivityFilterCategory, number>

const COUNTS_STALE_TIME_MS = 30_000

export function timelineItemCountsQueryKey(owner: string, repo: string, number: number) {
  return ['issue-viewer', 'timeline-item-counts', {owner, repo, number}] as const
}

export async function fetchTimelineItemCounts(
  {owner, repo, number}: {owner: string; repo: string; number: number},
  signal?: AbortSignal,
): Promise<ActivityFilterCounts> {
  const search = new URLSearchParams({queryId: IssueTimelineItemCounts})
  const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/${number}/timeline_item_counts?${search}`, {
    signal,
  })
  if (!response.ok) throw new Error(`Timeline item counts fetch failed (${response.status})`)

  const json = (await response.json()) as {data?: IssueTimelineItemCountsQuery}
  const issue = json.data?.repository?.issue
  if (!issue) throw new Error('Timeline item counts query returned no data')

  return Object.fromEntries(
    ACTIVITY_FILTER_CATEGORIES.map(category => [category.id, issue[category.id].totalCount]),
  ) as ActivityFilterCounts
}

/** Counts per filter category. Only read while `enabled`, since each count walks that part of the timeline. */
export function useTimelineItemCounts(owner: string, repo: string, number: number, enabled: boolean) {
  return useQuery({
    queryKey: timelineItemCountsQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchTimelineItemCounts({owner, repo, number}, signal),
    enabled,
    staleTime: COUNTS_STALE_TIME_MS,
  })
}
